import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import DealerAddressDropdown from '../../../../../components/common/DealerAddressDropdown';
import DealerDropdown from '../../../../../components/common/DealerDropdown';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { validateTCNumber } from '../../../../../utils/validationUtils';
import { useGetCountryByKunnrQuery } from '../../../../../api/endpoints/country';
import { InputNumber } from 'primereact/inputnumber';

const buildLockedDealerValue = (id, name) => {
    const label = name || String(id);
    return { id, name: label, text: label, erpDealerId: id, displayValue: label };
};

const UserAccountsForm = ({ visible, onHide, onSubmit, onRemove, selectedUser, loading, usageTypeOptions = [], isEditMode = false, isSubmitting = false, isDeleting = false,  onFormDataChange, lockedDealerId, lockedDealerName, lockedAddressId }) => {
    const { getCurrentLanguageId, th } = useLanguage();
    const toast = useRef(null);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [originalFormData, setOriginalFormData] = useState(null);

    const dealerLocked = lockedDealerId !== undefined && lockedDealerId !== null;
    const addressLocked = lockedAddressId !== undefined && lockedAddressId !== null;

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        passwordConfirmed: '',
        role: 'admin',
        dealerId: null,
        addrId: null,
        usageType: 0,
        identityNo: '',
        languageId: 2,
        isActive: 1,
        isEmailEnabled: true,
        countryName: '',
        country: '',
        phoneNumber: ''
    });

    const activeStatusOptions = [
        { label: th('userAccounts.active'), value: 1 },
        { label: th('userAccounts.inactive'), value: 0 }
    ];

    const isFormDisabled = isSubmitting || isDeleting || loading;

    const processingMessage = isSubmitting ? th('userAccounts.saving') : isDeleting ? th('userAccounts.deleting') : loading ? th('common.loading') : '';

    const normalizeFormData = (data) => {
        if (!data) return null;

        return {
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            role: data.role || 'admin',
            dealerId: typeof data.dealerId === 'object' ? data.dealerId?.id : data.dealerId,
            addrId: data.addrId || null,
            usageType: data.usageType || 0,
            identityNo: data.identityNo || '',
            languageId: data.languageId || 2,
            isActive: data.isActive !== undefined ? data.isActive : 1,
            isEmailEnabled: data.isEmailEnabled !== undefined ? data.isEmailEnabled : true
        };
    };

    const checkFormChanged = useCallback(() => {
        if (!originalFormData || !isEditMode) return false;

        const current = normalizeFormData(formData);
        const original = normalizeFormData(originalFormData);

        if (!current || !original) return false;

        const fieldsToCompare = ['firstName', 'lastName', 'email', 'dealerId', 'addrId', 'usageType', 'identityNo', 'languageId', 'isActive', 'isEmailEnabled'];

        // Check if password fields have values in edit mode
        const passwordChanged = isEditMode && (formData.password || formData.passwordConfirmed);

        return (
            passwordChanged ||
            fieldsToCompare.some((field) => {
                const currentValue = current[field];
                const originalValue = original[field];

                if (typeof currentValue === 'string' && typeof originalValue === 'string') {
                    return currentValue.trim() !== originalValue.trim();
                }

                return currentValue !== originalValue;
            })
        );
    }, [formData, originalFormData, isEditMode]);

    useEffect(() => {
        if (onFormDataChange) {
            onFormDataChange(formData);
        }
    }, [formData, onFormDataChange]);

    useEffect(() => {
        if (visible && selectedUser) {
            const paddedIdentityNo = selectedUser.identityNo ? String(selectedUser.identityNo).padStart(11, '0') : '';

            const dealerValueFromUser = selectedUser.dealerId
                ? {
                      id: selectedUser.dealerId,
                      name: selectedUser.dealerName || '',
                      text: selectedUser.dealerName || '',
                      erpDealerId: selectedUser.dealerId,
                      displayValue: selectedUser.dealerName || ''
                  }
                : null;

            const initialData = {
                ...selectedUser,
                firstName: selectedUser.firstName || '',
                lastName: selectedUser.lastName || '',
                email: selectedUser.email || '',
                role: selectedUser.role || 'admin',
                dealerId: dealerValueFromUser,
                addrId: selectedUser.addrId || null,
                usageType: selectedUser.usageType || 0,
                identityNo: paddedIdentityNo,
                languageId: selectedUser.languageId || 2,
                isActive: selectedUser.isActive !== undefined ? selectedUser.isActive : 1,
                isEmailEnabled: selectedUser.isEmailEnabled != null ? !!selectedUser.isEmailEnabled : (selectedUser.is_email_enabled != null ? !!selectedUser.is_email_enabled : true),
                password: '',
                passwordConfirmed: '',
                countryName: selectedUser.countryName || '',
                country: selectedUser.country || '',
                phoneNumber: selectedUser.phoneNumber || ''
            };

            const initialWithLocks = {
                ...initialData,
                dealerId: dealerLocked ? buildLockedDealerValue(lockedDealerId, lockedDealerName || selectedUser.dealerName) : initialData.dealerId,
                addrId: addressLocked ? lockedAddressId : initialData.addrId
            };

            setFormData(initialWithLocks);
            setOriginalFormData(initialWithLocks);
        } else if (visible && !selectedUser) {
            const newFormData = {
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                passwordConfirmed: '',
                role: 'admin',
                dealerId: null,
                addrId: null,
                usageType: 0,
                identityNo: '',
                languageId: 2,
                isActive: 1,
                isEmailEnabled: true
            };

            // NEW: new form’da da kilitleri uygula
            setFormData({
                ...newFormData,
                dealerId: dealerLocked ? buildLockedDealerValue(lockedDealerId, lockedDealerName) : newFormData.dealerId,
                addrId: addressLocked ? lockedAddressId : newFormData.addrId
            });
            setOriginalFormData(null);
        }

        if (visible) {
            setErrors({});
            setTouched({});
            setFormSubmitted(false);
        }
    }, [visible, selectedUser, lockedDealerId, lockedDealerName, lockedAddressId, dealerLocked, addressLocked]);

    const selectedDealerId = (formData.dealerId && typeof formData.dealerId === 'object' ? formData.dealerId.id : formData.dealerId) || null;

    const shouldFetchCountry = !!selectedDealerId;

    const lang = Number(getCurrentLanguageId?.());

    const { data: countryRes, isFetching: isFetchingCountry, error: countryError } = useGetCountryByKunnrQuery({ dealerId: selectedDealerId, languageId: lang === 1 || lang === 2 ? lang : 1 }, { skip: !shouldFetchCountry });

    useEffect(() => {
        if (countryRes && shouldFetchCountry) {
            setFormData((prev) => ({
                ...prev,
                countryName: countryRes.countryName || '',
                country: countryRes.countryCode || ''
            }));
        }
    }, [countryRes, shouldFetchCountry]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = th('userAccounts.fieldRequired');
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = th('userAccounts.fieldRequired');
        }

        if (!formData.email.trim()) {
            newErrors.email = th('userAccounts.fieldRequired');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = th('userAccounts.invalidEmail');
        }

        // Password validation for new users or edit mode (optional for edit)
        if (!isEditMode) {
            // Required for new users
            if (!formData.password) {
                newErrors.password = th('userAccounts.fieldRequired');
            } else if (formData.password.length < 8) {
                newErrors.password = th('userAccounts.passwordMinLength');
            }

            if (!formData.passwordConfirmed) {
                newErrors.passwordConfirmed = th('userAccounts.fieldRequired');
            } else if (formData.password !== formData.passwordConfirmed) {
                newErrors.passwordConfirmed = th('userAccounts.passwordMismatch');
            }
        } else {
            // Optional for edit mode, but if provided, validate
            if (formData.password) {
                if (formData.password.length < 8) {
                    newErrors.password = th('userAccounts.passwordMinLength');
                }

                if (!formData.passwordConfirmed) {
                    newErrors.passwordConfirmed = th('userAccounts.fieldRequired');
                } else if (formData.password !== formData.passwordConfirmed) {
                    newErrors.passwordConfirmed = th('userAccounts.passwordMismatch');
                }
            } else if (formData.passwordConfirmed) {
                // If password confirm is filled but password is empty
                newErrors.password = th('userAccounts.fieldRequired');
            }
        }

        if (formData.identityNo && !validateTCNumber(formData.identityNo)) {
            newErrors.identityNo = th('userAccounts.invalidIdentityNo');
        }

        const dealerId = formData.dealerId?.id || formData.dealerId;
        if (!dealerId) {
            newErrors.dealerId = th('userAccounts.fieldRequired');
        }

        if (!Number(formData.usageType)) {
            newErrors.usageType = th('userAccounts.fieldRequired');
        }

        if (!formData.phoneNumber || !formData.phoneNumber.trim()) {
            newErrors.phoneNumber = th('userAccounts.fieldRequired');
        } else {
            const digits = formData.phoneNumber.replace(/\D/g, '');
            if (digits.length < 7 || digits.length > 15) {
                newErrors.phoneNumber = th('userAccounts.invalidPhone');
            }
        }

        if (!formData.country) newErrors.countryName = th('userAccounts.fieldRequired');

        if (shouldFetchCountry && (isFetchingCountry || countryError)) {
            newErrors.countryName = th('userAccounts.countryNotReady');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleDialogHide = () => {
        if (isFormDisabled) {
            toast.current?.show({
                severity: 'warn',
                summary: th('common.warning'),
                detail: th('userAccounts.operationInProgress'),
                life: 3000
            });
            return;
        }

        if (isEditMode && checkFormChanged()) {
            setShowUnsavedChangesDialog(true);
        } else {
            onHide();
        }
    };

    const handleConfirmExit = () => {
        setShowUnsavedChangesDialog(false);
        onHide();
    };

    const handleCancelExit = () => {
        setShowUnsavedChangesDialog(false);
    };

    const handleSubmit = async () => {
        setFormSubmitted(true); 

        const allFields = Object.keys(formData);
        const newTouched = {};
        allFields.forEach((field) => {
            newTouched[field] = true;
        });
        setTouched(newTouched);

        if (!validateForm()) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('userAccounts.fillRequiredFields'),
                life: 3000
            });
            return;
        }

        try { 

            const submitData = {
                ...formData,
                dealerId: formData.dealerId?.id || 0,
                id: selectedUser?.id,
                userId: selectedUser?.userId,
                isActive: formData.isActive
            };

            // Include password fields if they have values (for both new and edit)
            if (formData.password && formData.passwordConfirmed) {
                submitData.password = formData.password;
                submitData.passwordConfirmed = formData.passwordConfirmed;
            }

            await onSubmit(submitData);
        } catch (error) {
            console.error('Error in form submission:', error);
        }
    };

    const handleDelete = async () => {
        try {
            await onRemove(selectedUser);
        } catch (error) {
            console.error('Error in delete operation:', error);
        }
    };

    const confirmDelete = () => {
        confirmDialog({
            message: th('userAccounts.deleteConfirmMessage'),
            header: th('userAccounts.deleteConfirmHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('common.yes'),
            rejectLabel: th('common.no'),
            accept: handleDelete,
            rejectClassName: 'p-button-outlined'
        });
    };

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const handleFieldBlur = (field) => {
        setTouched((prev) => ({
            ...prev,
            [field]: true
        }));
    };

    const handleDealerChange = useCallback(
        (dealer) => {
            if (dealerLocked) return;
            setFormData((prev) => ({
                ...prev,
                dealerId: dealer,
                addrId: null,
                countryName: '',
                country: ''
            }));

            if (errors.dealerId) {
                setErrors((prev) => ({
                    ...prev,
                    dealerId: undefined
                }));
            }
        },
        [errors.dealerId, dealerLocked]
    );

    const handleAddressChange = useCallback(
        (value) => {
            if (addressLocked) return;
            setFormData((prev) => ({
                ...prev,
                addrId: value?.id || null
            }));
        },
        [addressLocked]
    );

    const renderFieldError = (fieldName) => {
        if ((touched[fieldName] || formSubmitted) && errors[fieldName]) {
            return <small className="p-error">{errors[fieldName]}</small>;
        }
        return null;
    };

    const dialogHeader = (
        <div className="flex align-items-center justify-content-between">
            <span className="text-xl font-semibold">{isEditMode ? th('userAccounts.editTitle') : th('userAccounts.newTitle')}</span>
            {isFormDisabled && (
                <div className="flex align-items-center gap-2">
                    <ProgressSpinner style={{ width: '20px', height: '20px' }} />
                    <span className="text-sm">{processingMessage}</span>
                </div>
            )}
        </div>
    );

    const renderFooter = () => {
        if (isEditMode) {
            return (
                <div className="flex justify-content-between gap-2">
                    <Button label={th('common.delete')} icon="pi pi-trash" onClick={confirmDelete} loading={isDeleting} severity="danger" disabled={isSubmitting || isFormDisabled} />
                    <div className="flex gap-2">
                        <Button label={th('common.cancel')} icon="pi pi-times" onClick={handleDialogHide} outlined disabled={isFormDisabled} type="button" />
                        <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={isSubmitting} disabled={isDeleting || isFormDisabled} type="button" />
                    </div>
                </div>
            );
        } else {
            return (
                <div className="flex justify-content-end gap-2">
                    <Button label={th('common.cancel')} icon="pi pi-times" onClick={handleDialogHide} outlined disabled={isFormDisabled} type="button" />
                    <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={isSubmitting} disabled={isFormDisabled} type="button" />
                </div>
            );
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <Dialog
                visible={visible}
                style={{ width: '85%', maxWidth: '1200px' }}
                header={dialogHeader}
                modal
                className="p-fluid user-accounts-dialog"
                footer={renderFooter()}
                onHide={handleDialogHide}
                closable={!isFormDisabled}
                closeOnEscape={!isFormDisabled}
                dismissableMask={false}
                maximizable
                blockScroll
            >
                {isFormDisabled && <Message severity="info" text={processingMessage} className="w-full mb-4" icon="pi pi-spin pi-spinner" />}

                <div className={`${isFormDisabled ? 'p-disabled' : ''}`}>
                    <div className="grid">
                        <div className="col-12">
                            <h5 className="section-title font-bold">
                                <i className="pi pi-user mr-2"></i>
                                {th('userAccounts.userInfo')}
                            </h5>
                            <Divider />
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="firstName" className="font-bold block mb-2">
                                    <i className="pi pi-user mr-1"></i>
                                    {th('userAccounts.firstName')}*
                                </label>
                                <InputText
                                    id="firstName"
                                    value={formData.firstName}
                                    onChange={(e) => handleFieldChange('firstName', e.target.value)}
                                    onBlur={() => handleFieldBlur('firstName')}
                                    className={(touched.firstName || formSubmitted) && errors.firstName ? 'p-invalid w-full' : 'w-full'}
                                    disabled={isFormDisabled}
                                    placeholder={th('userAccounts.enterFirstName')}
                                />
                                {renderFieldError('firstName')}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="lastName" className="font-bold block mb-2">
                                    <i className="pi pi-user mr-1"></i>
                                    {th('userAccounts.lastName')}*
                                </label>
                                <InputText
                                    id="lastName"
                                    value={formData.lastName}
                                    onChange={(e) => handleFieldChange('lastName', e.target.value)}
                                    onBlur={() => handleFieldBlur('lastName')}
                                    className={(touched.lastName || formSubmitted) && errors.lastName ? 'p-invalid w-full' : 'w-full'}
                                    disabled={isFormDisabled}
                                    placeholder={th('userAccounts.enterLastName')}
                                />
                                {renderFieldError('lastName')}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="identityNo" className="font-bold block mb-2">
                                    <i className="pi pi-id-card mr-1"></i>
                                    {th('userAccounts.identityNo')}
                                </label>
                                <InputText
                                    id="identityNo"
                                    type="text"
                                    pattern="\d{11}"
                                    inputMode="numeric"
                                    value={formData.identityNo}
                                    onChange={(e) => {
                                        let value = e.target.value.replace(/\D/g, '');
                                        if (value.length > 11) {
                                            value = value.slice(0, 11);
                                        }
                                        handleFieldChange('identityNo', value);
                                    }}
                                    onBlur={(e) => {
                                        handleFieldBlur('identityNo');
                                        let value = e.target.value;
                                        if (value && value.length < 11) {
                                            value = value.padStart(11, '0');
                                            handleFieldChange('identityNo', value);
                                        }
                                    }}
                                    className={(touched.identityNo || formSubmitted) && (errors.identityNo || (formData.identityNo && !validateTCNumber(formData.identityNo))) ? 'p-invalid w-full' : 'w-full'}
                                    placeholder="01234567890"
                                    disabled={isFormDisabled}
                                />
                                {renderFieldError('identityNo')}
                                {formData.identityNo && !validateTCNumber(formData.identityNo) && (touched.identityNo || formSubmitted) && <small className="p-error">{th('userAccounts.invalidIdentityNo')}</small>}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="email" className="font-bold block mb-2">
                                    <i className="pi pi-envelope mr-1"></i>
                                    {th('userAccounts.email')}*
                                </label>
                                <InputText
                                    id="email"
                                    value={formData.email}
                                    onChange={(e) => handleFieldChange('email', e.target.value)}
                                    onBlur={() => handleFieldBlur('email')}
                                    className={(touched.email || formSubmitted) && errors.email ? 'p-invalid w-full' : 'w-full'}
                                    disabled={isEditMode || isFormDisabled}
                                    placeholder={th('userAccounts.enterEmail')}
                                />
                                {renderFieldError('email')}
                            </div>
                        </div>

                        <div className="col-12 md:col-6">
                            <div className="field mb-3">
                                <label htmlFor="phoneNumber" className="font-bold block mb-2">
                                    <i className="pi pi-phone mr-1"></i>
                                    {th('customerDefinition.formLabels.phone')}*
                                </label>

                                <InputNumber
                                    id="phoneNumber"
                                    value={formData.phoneNumber ? Number(formData.phoneNumber) : null}
                                    onValueChange={(e) => {
                                        const raw = e.originalEvent?.target?.value ?? '';
                                        const digits = raw.replace(/\D/g, '');
                                        handleFieldChange('phoneNumber', digits);
                                    }}
                                    onBlur={() => handleFieldBlur('phoneNumber')}
                                    useGrouping={false}
                                    minFractionDigits={0}
                                    maxFractionDigits={0}
                                    inputMode="numeric"
                                    className={(touched.phoneNumber || formSubmitted) && errors.phoneNumber ? 'p-invalid w-full' : 'w-full'}
                                    disabled={isFormDisabled}
                                    placeholder={th('userAccounts.phonePlaceholder')}
                                />
                                {renderFieldError('phoneNumber')}
                            </div>
                        </div>
                        {isEditMode && (
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="isActive" className="font-bold block mb-2">
                                        <i className="pi pi-check-circle mr-1"></i>
                                        {th('userAccounts.status')}
                                    </label>
                                    <Dropdown
                                        id="isActive"
                                        value={formData.isActive}
                                        options={activeStatusOptions}
                                        onChange={(e) => handleFieldChange('isActive', e.value)}
                                        placeholder={th('userAccounts.selectStatus')}
                                        className="w-full"
                                        disabled={isFormDisabled}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label className="font-bold block mb-2">
                                    <i className="pi pi-envelope mr-1"></i>
                                    {th('userAccounts.emailNotificationEnabled')}
                                </label>
                                <div className="flex align-items-center mt-1">
                                    <Checkbox
                                        inputId="isEmailEnabled"
                                        checked={!!formData.isEmailEnabled}
                                        onChange={(e) => handleFieldChange('isEmailEnabled', e.checked)}
                                        disabled={isFormDisabled}
                                    />
                                    <label htmlFor="isEmailEnabled" className="ml-2">
                                        {formData.isEmailEnabled ? th('userAccounts.emailEnabled') : th('userAccounts.emailDisabled')}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <label className="font-bold block mb-2">
                                <i className="pi pi-tag mr-1"></i>
                                {th('userAccounts.usageType')}*
                            </label>
                            <div className="flex flex-wrap gap-3">
                                {usageTypeOptions.map((option) => (
                                    <div key={option.code} className="flex align-items-center">
                                        <RadioButton
                                            inputId={`usageType${option.code}`}
                                            name="usageType"
                                            value={Number(option.code)}
                                            onChange={(e) => handleFieldChange('usageType', e.value)}
                                            checked={Number(formData.usageType) === Number(option.code)}
                                            disabled={isFormDisabled}
                                        />
                                        <label htmlFor={`usageType${option.code}`} className="ml-2">
                                            {option.translatedText}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            {renderFieldError('usageType')}
                        </div>
                        <>
                            <div className="col-12">
                                <h5 className="section-title font-bold">
                                    <i className="pi pi-lock mr-2"></i>
                                    {th('userAccounts.updatePassword')}
                                </h5>
                                <Divider />
                                {isEditMode && <Message severity="info" text={th('userAccounts.passwordChangeOptionalMessage')} className="w-full" icon="pi pi-info-circle" />}
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="password" className="font-bold block mb-2">
                                        <i className="pi pi-key mr-1"></i>
                                        {th('userAccounts.password')}
                                        {!isEditMode && '*'}
                                    </label>
                                    <Password
                                        id="password"
                                        value={formData.password}
                                        onChange={(e) => handleFieldChange('password', e.target.value)}
                                        onBlur={() => handleFieldBlur('password')}
                                        className={(touched.password || formSubmitted) && errors.password ? 'p-invalid w-full' : 'w-full'}
                                        feedback={true}
                                        toggleMask
                                        promptLabel={th('userAccounts.enterPassword')}
                                        weakLabel={th('userAccounts.passwordWeak')}
                                        mediumLabel={th('userAccounts.passwordMedium')}
                                        strongLabel={th('userAccounts.passwordStrong')}
                                        disabled={isFormDisabled}
                                        placeholder={th('userAccounts.enterPassword')}
                                    />
                                    {renderFieldError('password')}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="passwordConfirmed" className="font-bold block mb-2">
                                        <i className="pi pi-key mr-1"></i>
                                        {th('userAccounts.passwordConfirm')}
                                        {!isEditMode && '*'}
                                    </label>
                                    <Password
                                        id="passwordConfirmed"
                                        value={formData.passwordConfirmed}
                                        onChange={(e) => handleFieldChange('passwordConfirmed', e.target.value)}
                                        onBlur={() => handleFieldBlur('passwordConfirmed')}
                                        className={(touched.passwordConfirmed || formSubmitted) && errors.passwordConfirmed ? 'p-invalid w-full' : 'w-full'}
                                        feedback={false}
                                        toggleMask
                                        disabled={isFormDisabled}
                                        placeholder={th('userAccounts.enterPasswordConfirm')}
                                    />
                                    {renderFieldError('passwordConfirmed')}
                                </div>
                            </div>
                        </>


                            <>
                                <div className="col-12">
                                    <h5 className="section-title font-bold">
                                        <i className="pi pi-shop mr-2"></i>
                                        {th('userAccounts.dealerInfo')}
                                    </h5>
                                    <Divider />
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field">
                                        <label htmlFor="dealer" className="font-bold block mb-2">
                                            <i className="pi pi-building mr-1"></i>
                                            {th('userAccounts.dealer')}*
                                        </label>
                                        <DealerDropdown
                                            value={dealerLocked ? (formData.dealerId && typeof formData.dealerId === 'object' && formData.dealerId.displayValue ? formData.dealerId : buildLockedDealerValue(lockedDealerId, lockedDealerName)) : formData.dealerId}
                                            onChange={handleDealerChange}
                                            className={(touched.dealerId || formSubmitted) && errors.dealerId ? 'p-invalid w-full' : 'w-full'}
                                            placeholder={th('userAccounts.dealerPlaceholder')}
                                            onBlur={() => handleFieldBlur('dealerId')}
                                            disabled={isFormDisabled || dealerLocked}
                                        />
                                        {renderFieldError('dealerId')}
                                    </div>
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field">
                                        <label htmlFor="address" className="font-bold block mb-2">
                                            <i className="pi pi-map-marker mr-1"></i>
                                            {th('userAccounts.address')}
                                        </label>
                                        <DealerAddressDropdown
                                            value={addressLocked ? lockedAddressId : formData.addrId}
                                            onChange={handleAddressChange}
                                            dealerId={formData.dealerId?.id || formData.dealerId}
                                            placeholder={th('userAccounts.addressPlaceholder')}
                                            disabled={!formData.dealerId || isFormDisabled || addressLocked}
                                        />
                                    </div>
                                </div>

                                <div className="col-12 md:col-4">
                                    <div className="field">
                                        <label className="font-bold block mb-2">
                                            <i className={`pi ${isFetchingCountry ? 'pi-spin pi-spinner' : 'pi-flag'} mr-1`} />
                                            {th('userAccounts.country')}*
                                        </label>
                                        <InputText
                                            value={formData.countryName || ''}
                                            className={(touched.countryName || formSubmitted) && errors.countryName ? 'p-invalid w-full' : 'w-full'}
                                            disabled
                                            placeholder={isFetchingCountry ? th('common.loading') : th('userAccounts.countryPlaceholder')}
                                        />
                                        {(touched.countryName || formSubmitted) && errors.countryName && <small className="p-error">{errors.countryName}</small>}
                                    </div>
                                </div>
                            </>
                    </div>
                </div>
            </Dialog>

            <Dialog
                visible={showUnsavedChangesDialog}
                style={{ width: '450px' }}
                header={th('userAccounts.unsavedChangesTitle')}
                modal
                onHide={handleCancelExit}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label={th('common.cancel')} icon="pi pi-times" onClick={handleCancelExit} outlined type="button" />
                        <Button label={th('userAccounts.confirmExit')} icon="pi pi-check" onClick={handleConfirmExit} severity="danger" />
                    </div>
                }
            >
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem', color: 'var(--orange-500)' }} />
                    <span>{th('userAccounts.unsavedChangesMessage')}</span>
                </div>
            </Dialog>
        </>
    );
};

export default UserAccountsForm;
